import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "typedefs";
import {
  CalendarPreferences,
  CalendarViewType,
  DEFAULT_CALENDAR_PREFERENCES,
} from "app/models/CalendarPreferences";
import {
  fetchCalendarPreferences,
  updatePreferences as updatePreferencesAction,
} from "app/actions/calendarPreferences";

const URL_VIEW_WHITELIST: CalendarViewType[] = [
  "timeGridDay",
  "timeGridWeek",
  "dayGridMonth",
  "listWeek",
];

// `?view=...` on the URL seeds the initial view for share-links.
function readViewFromUrl(): CalendarViewType | null {
  const view = new URLSearchParams(window.location.search).get(
    "view",
  ) as CalendarViewType | null;
  return view && URL_VIEW_WHITELIST.includes(view) ? view : null;
}

export default function useCalendarPreferences() {
  const dispatch = useDispatch();
  const coachId = useSelector((state: RootState) => state.user.self?.id);
  const slice = useSelector((state: RootState) => state.calendarPreferences);

  useEffect(() => {
    const urlView = readViewFromUrl();
    if (urlView) {
      dispatch(updatePreferencesAction({ calendarView: urlView }));
    }
  }, [dispatch]);

  useEffect(() => {
    if (!coachId) {
      return;
    }
    dispatch(fetchCalendarPreferences() as any);
  }, [dispatch, coachId]);

  /**
   * CFTB-3431: on a coach's public calendar there is no signed-in reader, so there
   * are no preferences of their own to fetch — and the coach's own settings, which
   * the calendar should follow, come with the public coach payload instead. Whose
   * settings win is decided here rather than in each screen, so the calendar, its
   * slots and the confirm bar cannot disagree.
   *
   * Signed in, nothing changes: the reader's own preferences apply, as before.
   */
  const publicCoach = useSelector(
    (state: RootState) => state.bookSession?.coach,
  );

  const preferences: CalendarPreferences = useMemo(() => {
    const base = slice.data ?? DEFAULT_CALENDAR_PREFERENCES;
    if (coachId || !publicCoach) {
      return base;
    }

    return {
      ...base,
      firstDay: publicCoach.calendarFirstDayOfWeek ?? base.firstDay,
      timeFormat: publicCoach.calendarTimeFormat ?? base.timeFormat,
      dateFormat: publicCoach.calendarDateFormat ?? base.dateFormat,
    };
  }, [slice.data, coachId, publicCoach]);

  const updatePreferences = useCallback(
    (partial: Partial<CalendarPreferences>) => {
      dispatch(updatePreferencesAction(partial));
    },
    [dispatch],
  );

  const refresh = useCallback(() => {
    return dispatch(fetchCalendarPreferences(true));
  }, [dispatch]);

  return {
    preferences,
    updatePreferences,
    refresh,
  };
}
